import { ForceDirectedLayoutType } from '@neo4j-nvl/base';
import { isNil, omit, pick } from 'lodash-es';

import type { Scene } from '../../types/scene';
import type { Style } from '../../types/style';
import { getFilterRules } from '../filter/filter';
import { getGdsState } from '../gds/gds';
import { getVisibleRelationships } from '../graph/relationships';
import { getRanges } from '../slicer/slicer';
import { STYLE_TYPE_SCENE, getStyleById } from '../styles/styles';
import type { RootState } from '../types';
import { getVizState, initialState } from '../visualization/visualization';
import { getLatestSceneVersion } from './migrations';
import { getCurrentNodePositions, getCurrentScene } from './scene.selectors';

export class SceneTransformer {
  keepPropertiesThatCanTriggerSceneSaving(fullScene: Scene): Partial<Scene> {
    return omit(fullScene, ['lastModified', 'visualisation.zoomLevel', 'visualisation.layoutType']);
  }

  getFullSceneFromCurrentGraph(state: RootState, scene?: Scene | null, style?: Partial<Style>): Scene {
    const sceneDetails = scene ?? getCurrentScene(state);
    const nodePositions = getCurrentNodePositions(state) ?? [];
    const relationships =
      nodePositions.length > 0 && !isNil(state?.graph?.present?.relationships?.visible)
        ? getVisibleRelationships(state).map(({ id }) => id)
        : [];
    const filters = getFilterRules(state) ?? [];
    const ranges = getRanges(state) ?? [];
    const gds = pick(getGdsState(state) ?? {}, ['gdsRules', 'gdsResults']);
    const visualisation = pick(getVizState(state) ?? {}, [
      'layoutType',
      'layoutOptions',
      'zoomLevel',
      'panCoordinates',
      'layoutDirection',
    ]);
    const styleToSave = style ?? getStyleById(state)(sceneDetails?.id);
    const version = getLatestSceneVersion();
    if (isNil(version)) throw new Error('Client version not available');

    return {
      ...omit(sceneDetails, 'temporaryData'),
      version,
      numOfNodes: nodePositions.length,
      numOfRels: relationships.length,
      visualisation: {
        layoutDirection: initialState.layoutOptions.forceDirected,
        zoomLevel: initialState.zoomLevel,
        layoutType: ForceDirectedLayoutType,
        ...visualisation,
      },
      nodes: nodePositions,
      filters,
      ranges: ranges.map((range) => ({
        ...range,
        isActive: false,
      })),
      gds,
      relationships,
      // @ts-expect-error cant infer valid scene type
      style: { type: STYLE_TYPE_SCENE, ...styleToSave },
    };
  }
}
